/**
 * How a token symbol and a network name are spelled on screen.
 *
 * =========================================================================
 * WHY A MODULE AND NOT `.toUpperCase()` AT EACH SITE
 * =========================================================================
 *
 * The API returns these in whatever case its own records happen to hold —
 * `pol`, `POL`, `usdt`, `polygon`, `BNB Smart Chain` — and the app rendered
 * them verbatim in most places and `.toUpperCase()`-ed them in about twenty
 * others. So the same asset read as `pol` in the deposit modal and `POL` in the
 * review beside it, on one screen.
 *
 * Twenty scattered `.toUpperCase()` calls were already the second convention;
 * a third would not have helped. This is the one answer, and the two halves of
 * it are deliberately NOT the same rule.
 *
 * =========================================================================
 * SYMBOLS ARE UPPERCASE. NAMES ARE NOT.
 * =========================================================================
 *
 * A ticker is uppercase everywhere in finance — POL, USDT, ETH — and it is
 * short, so shouting it costs nothing and matches every exchange the user has
 * seen.
 *
 * A network NAME is a proper noun, and the brands write themselves in title
 * case: Polygon, Ethereum, BNB Smart Chain. `BNB SMART CHAIN` in the middle of
 * a sentence is not consistency, it is a different mistake — so names are
 * title-cased, and anything that already carries a capital is left exactly as
 * it is. That last rule is what protects the acronyms: `BNB Smart Chain` has
 * capitals already, so nothing touches it, and a naive title-caser that lowered
 * the rest of each word would have produced `Bnb Smart Chain`.
 *
 * PERSIAN IS NEVER TOUCHED. `fa_name` reaches these functions on the Farsi
 * locale, and neither `toUpperCase` nor title casing means anything for Arabic
 * script — the guard is the has-a-capital test for names (Persian has no case,
 * so it falls to the title-caser) plus an explicit Latin-only check.
 */

/** Latin letters only — the test that keeps casing rules off Persian text. */
const HAS_LATIN = /[A-Za-z]/;
const HAS_UPPERCASE = /[A-Z]/;

/**
 * A token ticker, as every exchange writes it: `pol` → `POL`.
 *
 * Returns an empty string for anything absent, so a caller can render it
 * directly without a `?? ""` of its own. Non-Latin input is returned untouched.
 */
export function formatTokenSymbol(value: string | null | undefined): string {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text || !HAS_LATIN.test(text)) return text;
  return text.toUpperCase();
}

/**
 * A network's name as its own brand writes it: `polygon` → `Polygon`,
 * `BNB Smart Chain` → `BNB Smart Chain`.
 *
 * ALREADY-CAPITALISED INPUT IS RETURNED VERBATIM. That is the whole acronym
 * rule: the backend already spells the awkward ones correctly, and the only
 * strings that need help are the all-lowercase ones it stores for the simple
 * chains. Rewriting a string that someone has already cased is how
 * `BNB Smart Chain` becomes `Bnb Smart Chain`.
 */
export function formatNetworkName(value: string | null | undefined): string {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text || !HAS_LATIN.test(text)) return text;
  if (HAS_UPPERCASE.test(text)) return text;
  return text.replace(/\b[a-z]/g, (letter) => letter.toUpperCase());
}
